import { Button, Dialog, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import { DangerousHtmlContent, variants } from '@eti/components';
import { useCallback, useState } from 'react';
import type { SortType } from '@eti/schema-types';
import RadioFilter from './RadioFilter';
import type { QuickSortOptionType } from './utils';

interface MobileSortProps {
  isOpen: boolean;
  onDone?: (sortType?: SortType) => void;
  onDismiss?: (value: boolean) => void;
  options: QuickSortOptionType[];
  sortPrices: {
    bestValue: number;
    carrierPromo: number;
    cheapTrip: number;
    recommendation: number;
    shortTrip: number;
  };
}

const dividerStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  left: 0;
  position: absolute;
  width: 100%;
`;

const submitButtonStyles = css`
  margin: 16px auto 0;
  width: 100%;

  @media (min-width: 48em) {
    max-width: 250px;
  }
`;

const filterWrapperStyles = css`
  flex: 0 0 100%;
  max-width: 100%;

  @media ${mediaQueries.medium.up} {
    flex: 0 0 calc(50% - 15px);
    max-width: calc(50% - 15px);
  }
`;
const noteDivStyles = css`
  color: #737373;
  font-size: 0.875rem;
  line-height: 150%;
`;

const quickSortDialogStyles = css`
  div:has(section) {
    box-shadow: 0 -2px 8px 0 #0000000d;
  }
`;

const MobileSort = ({ isOpen, options, onDismiss, onDone, sortPrices }: MobileSortProps) => {
  const { t } = useTranslation();
  const [selectedSort, setSelectedSort] = useState(undefined);

  const handleChange = useCallback(
    (event) => {
      const { value } = event.target;
      setSelectedSort(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSort],
  );

  const handleDoneClick = useCallback(() => {
    if (onDone && onDismiss) {
      onDone(selectedSort);
      onDismiss(false);
    }
  }, [onDone, onDismiss, selectedSort]);

  return (
    <Dialog
      className={quickSortDialogStyles}
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="sorting-dialog-container"
      footer={
        <section className={filterWrapperStyles}>
          <div className={noteDivStyles}>
            <DangerousHtmlContent content={t('Result.QuickSort.Info.Text')} />
          </div>
          <Button
            className={submitButtonStyles}
            onClick={handleDoneClick}
            variant={variants.PRIMARY}
          >
            {t('Result.SearchFilters.Done.Button')}
          </Button>
        </section>
      }
      isOpen={isOpen}
      onDismiss={() => onDismiss?.(false)}
      title={t('Result.Responsive.Sort.Title')}
    >
      <>
        <div className={dividerStyles} />
        {options.map((option, index) => (
          <RadioFilter
            key={index}
            onChange={handleChange}
            option={option}
            selectedValue={selectedSort}
            sortPrices={sortPrices}
          />
        ))}
      </>
    </Dialog>
  );
};

export default MobileSort;
